/**
 * Chat Message Component
 * 
 * Displays individual chat messages from user or AI Assistant.
 * Supports rich content rendering (customer picker, slot grid, approval card, task timeline).
 */
import { useState } from "react";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import logoSvg from "@assets/svgs/logo.svg";
import type { ChatMessage as ChatMessageType } from "@/types/teeAgent";
import { TaskTimeline } from "./TaskTimeline";
import { CustomerPickerCard } from "./CustomerPickerCard";
import { SlotGrid } from "./SlotGrid";
import { ApprovalCard } from "./ApprovalCard";
import { GuardrailPrompt } from "./GuardrailPrompt";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ChatMessageProps {
  message: ChatMessageType;
  onSuggestionClick?: (text: string) => void;
  onCustomerSelect?: (id: string) => void;
  onSlotSelect?: (key: string) => void;
  onApprove?: (fields: any[]) => void;
  onReject?: () => void;
  onGuardrailChoose?: (level: "auto" | "ask" | "never") => void;
}

export const ChatMessage = ({
  message,
  onSuggestionClick,
  onCustomerSelect,
  onSlotSelect,
  onApprove,
  onReject,
  onGuardrailChoose,
}: ChatMessageProps) => {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Don't render system messages
  if (message.role === "system") {
    return null;
  }

  const handleCustomerSelect = (id: string) => {
    setSelectedCustomer(id);
    onCustomerSelect?.(id);
  };

  const handleSlotSelect = (key: string) => {
    setSelectedSlot(key);
    onSlotSelect?.(key);
  };

  const rich = message.richContent;

  return (
    <div
      className={cn(
        "flex gap-2 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-300 fill-mode-backwards",
        isUser && "justify-end"
      )}
    >
      {/* Avatar for assistant */}
      {isAssistant && (
        <div className="pt-1 shrink-0">
          <div className="w-6 h-6 rounded-full bg-green4 flex items-center justify-center border border-green3/40">
            <img src={logoSvg} alt="Tee Agent" className="h-3.5 w-3.5" />
          </div>
        </div>
      )}

      {/* Message Content */}
      <div className={cn("flex-1", isUser ? "max-w-[85%] flex justify-end" : "max-w-[92%]")}>
        <div
          className={cn(
            "rounded-2xl px-3.5 py-2.5 text-[13px]",
            isUser && "bg-black1 text-white rounded-tr-sm",
            isAssistant && "bg-white border border-gray6 text-gray1 rounded-tl-sm"
          )}
        >
          {/* Text content */}
          {isUser ? (
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          ) : (
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
                  li: ({ node, children, ...props }) => {
                    // Extract plain text from children for the click handler
                    const extractText = (child: any): string => {
                      if (typeof child === "string") return child;
                      if (child?.props?.children) {
                        const nested = child.props.children;
                        if (Array.isArray(nested)) return nested.map(extractText).join("");
                        return extractText(nested);
                      }
                      return "";
                    };
                    const text = Array.isArray(children)
                      ? children.map(extractText).join("").trim()
                      : extractText(children).trim();
                    const isSuggestion = text.endsWith("?");

                    return (
                      <li
                        className={cn(
                          "pl-1",
                          isSuggestion && onSuggestionClick &&
                            "cursor-pointer rounded-md px-2 py-1 -mx-1 hover:bg-green4 hover:text-green2 transition-colors duration-150"
                        )}
                        onClick={
                          isSuggestion && onSuggestionClick
                            ? () => onSuggestionClick(text)
                            : undefined
                        }
                        {...props}
                      >
                        {children}
                      </li>
                    );
                  },
                  p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                  strong: ({ node, ...props }) => <span className="font-semibold text-gray-900" {...props} />,
                  h1: ({ node, ...props }) => <h1 className="text-lg font-bold mb-2 mt-4 first:mt-0" {...props} />,
                  h2: ({ node, ...props }) => <h2 className="text-base font-bold mb-2 mt-3" {...props} />,
                  h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2" {...props} />,
                  code: ({ node, className, children, ...props }: any) => {
                    const match = /language-(\w+)/.exec(className || "");
                    const isInline = !match && !className?.includes("language-");
                    return isInline ? (
                      <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono text-red-500" {...props}>
                        {children}
                      </code>
                    ) : (
                      <div className="bg-gray-900 text-gray-100 p-2 rounded-md my-2 text-xs overflow-x-auto">
                        <code className="font-mono" {...props}>{children}</code>
                      </div>
                    );
                  },
                  table: ({ node, ...props }) => (
                    <div className="overflow-x-auto my-2 border border-gray-200 rounded-lg">
                      <table className="w-full text-left text-xs" {...props} />
                    </div>
                  ),
                  thead: ({ node, ...props }) => <thead className="bg-gray-50 border-b border-gray-200" {...props} />,
                  th: ({ node, ...props }) => <th className="px-3 py-2 font-semibold text-gray-700" {...props} />,
                  td: ({ node, ...props }) => <td className="px-3 py-2 border-b border-gray-100 last:border-0" {...props} />,
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {/* Rich content rendering */}
          {rich?.type === "task_timeline" && rich.taskTimeline && (
            <TaskTimeline steps={rich.taskTimeline.steps} />
          )}

          {rich?.type === "customer_picker" && rich.customerPicker && (
            <CustomerPickerCard
              customers={rich.customerPicker.customers}
              selectedId={selectedCustomer}
              onSelect={handleCustomerSelect}
              label={rich.customerPicker.label}
            />
          )}

          {rich?.type === "slot_grid" && rich.slotGrid && (
            <SlotGrid
              slots={rich.slotGrid.slots}
              selected={selectedSlot}
              onSelect={handleSlotSelect}
              label={rich.slotGrid.label}
            />
          )}

          {rich?.type === "approval_card" && rich.approvalCard && (
            <ApprovalCard
              title={rich.approvalCard.title}
              fields={rich.approvalCard.fields}
              total={rich.approvalCard.total}
              riskLevel={rich.approvalCard.riskLevel}
              onApprove={onApprove || (() => {})}
              onReject={onReject || (() => {})}
            />
          )}

          {rich?.type === "guardrail_prompt" && rich.guardrailPrompt && (
            <GuardrailPrompt
              actionLabel={rich.guardrailPrompt.actionLabel}
              onChoose={onGuardrailChoose || (() => {})}
            />
          )}

          {/* Timestamp */}
          <div
            className={cn(
              "text-[10px] mt-1.5 font-medium tracking-wide",
              isUser && "text-white/50 text-right",
              isAssistant && "text-gray4"
            )}
          >
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      </div>

      {/* Avatar for user */}
      {isUser && (
        <div className="pt-1 shrink-0">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <User className="h-3 w-3 text-white" />
          </div>
        </div>
      )}
    </div>
  );
};

